
<script setup>
const { $t } = useNuxtApp()

const props = defineProps({
    options: {
        type: Object,
        default: () => ({})
    },
    // Accessible label for the flipbook
    ariaLabel: {
        type: String,
        default: ''
    },
    // Show download link for screen readers
    showAccessibleAlternative: {
        type: Boolean,
        default: true
    }
})

// Get PDF source from options for accessible download link
const pdfSource = computed(() => props.options?.source || '')

let flipbookInstance = null
const elementRef = ref(null)

const initializeFlipbook = () => {
    if (!window.DEARFLIP || !elementRef.value) return

    try {
        // Set correct asset locations
        window.dFlipLocation = '/dflip/'

        // Create the flipbook instance using jQuery
        const $element = window.DEARFLIP.jQuery(elementRef.value)
        flipbookInstance = $element.dearviewer(props.options)
    } catch (error) {
        //
    }
}

const waitForDearFlip = () => {
    if (window.DEARFLIP && window.DEARFLIP.jQuery) {
        initializeFlipbook()
    } else {
        // Wait for the library to load
        setTimeout(waitForDearFlip, 100)
    }
}

onMounted(() => {
    nextTick(() => {
        waitForDearFlip()
    })
})

onBeforeUnmount(() => {
    // Cleanup when component is destroyed
    if (flipbookInstance && typeof flipbookInstance.dispose === 'function') {
        flipbookInstance.dispose()
        flipbookInstance = null
    }
})

defineExpose({
    getFlipbookInstance: () => flipbookInstance
})
</script>

<template>
  <div class="df-wrapper">
    <!-- Accessible alternative for screen readers -->
    <div
      v-if="showAccessibleAlternative && pdfSource"
      class="sr-only"
      role="region"
      aria-label="PDF dokument"
    >
      <p>{{ $t('Interaktívny prehliadač PDF dokumentu.') }}</p>
      <a
        :href="pdfSource"
        target="_blank"
        rel="noopener"
        class="pdf-download-link"
      >
        {{ $t('Otvoriť PDF v novom okne (prístupné pre čítačky obrazovky)') }}
      </a>
    </div>

    <!-- Visual flipbook viewer - hidden from screen readers since accessible alternative provided -->
    <div
      ref="elementRef"
      class="df-element"
      :aria-hidden="showAccessibleAlternative ? 'true' : undefined"
      :aria-label="!showAccessibleAlternative && ariaLabel ? ariaLabel : undefined"
      :role="!showAccessibleAlternative ? 'application' : undefined"
    />
  </div>
</template>

<style scoped>
.df-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.df-element {
  width: 100%;
  height: 100% !important;
  max-height: 100% !important;
  position: relative;
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Make download link visible when focused for sighted keyboard users */
.sr-only:focus-within {
  position: static;
  width: auto;
  height: auto;
  padding: 1rem;
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: normal;
  background: var(--global-color-white, #fff);
  border: 2px solid var(--global-color-secondary, #00529b);
  border-radius: 0.5rem;
  z-index: 1000;
}

.pdf-download-link {
  color: var(--global-color-secondary, #00529b);
  text-decoration: underline;
  font-weight: bold;
}

.pdf-download-link:focus {
  outline: 2px solid var(--global-color-secondary, #00529b);
  outline-offset: 2px;
}
</style>
